/**
 * Page: EditVideoTemplate
 * Admin-only page to edit a video template. Loads the template and submits
 * updates to the API.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import { ArrowLeft } from 'lucide-react';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import VideoTemplateForm from '../../../components/admin/videoTemplates/VideoTemplateForm';
import Footer from '../../../components/shared/Footer';
import { getVideoTemplate, updateVideoTemplate } from '../../../api/content/videoTemplatesApi';
import { isUserAdmin } from '../../../utils/admin/adminUtils';

export default function EditVideoTemplate() {
  const navigate = useNavigate();
  const { videoTemplateId } = useParams();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [submitError, setSubmitError] = useState('');
  const [videoTemplate, setVideoTemplate] = useState(null);
  const [loadError, setLoadError] = useState('');

  // Check if user is the admin
  const isAdmin = isUserAdmin(user);

  // Check if user is an admin, if not, redirect to home page
  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      } else {
        loadVideoTemplate();
      }
    } else if (user === null) {
      // No user, redirect to login
      navigate('/login');
    }
  }, [navigate, user, isAdmin, videoTemplateId]);

  const loadVideoTemplate = async () => {
    if (!videoTemplateId) {
      setLoadError('Invalid video template ID');
      setIsLoading(false);
      return;
    }

    try {
      const response = await getVideoTemplate(videoTemplateId);
      
      if (response && response.videoTemplate) {
        setVideoTemplate(response.videoTemplate);
      } else {
        throw new Error('Video template not found');
      }
    } catch (error) {
      setLoadError('Failed to load video template. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const response = await updateVideoTemplate(videoTemplateId, formData);
      
      if (response) {
        addToast('Video template has been updated successfully!', 'success');
        navigate('/app/private/manageVideoTemplates');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      let errorMessage = 'Failed to update video template. Please try again.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSubmitError(errorMessage);
      addToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading video template..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Redirect happens in useEffect
  }

  if (loadError) {
    return (
      <div className="p-6 w-full max-w-[1600px] mx-auto">
        <div className="mb-4">
          <button
            onClick={() => navigate('/app/private/manageVideoTemplates')}
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back to Video Templates
          </button>
        </div>
        
        <div className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm p-6">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Error Loading Video Template</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{loadError}</p>
            <button
              onClick={loadVideoTemplate}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto">
      {/* Back to Video Templates button */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/manageVideoTemplates')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Video Templates
        </button>
      </div>

      <div className="mb-6 flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Edit Video Template</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Update the video template information and settings.
          </p>
        </div>
        <button
          onClick={() => navigate('/app/private/manageVideoTemplates')}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700"
        >
          Manage Video Templates
        </button>
      </div>
      
      <div className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm p-6">
        {submitError && (
          <div className="mb-4 rounded-md bg-red-50 p-4 dark:bg-red-900/20">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400 dark:text-red-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error Updating Video Template</h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  {submitError}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {videoTemplate && (
          <VideoTemplateForm 
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            initialData={videoTemplate}
            submitButtonText="Update Video Template"
          />
        )}
      </div>

      <Footer />
    </div>
  );
}
